import { Dispatch, SetStateAction } from "react";
import { COLORS } from "../../constants/design/colors";
import {
  GetAccount200ResponsePositionsInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { getTxExplorerUrl } from "../../utils/chain";
import { formatContractError } from "../../utils/errors";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { useOrder } from "../../hooks/api/order/useOrder";
import { usePerpOrder } from "../../hooks/api/order/usePerpOrder";
import { useToast } from "../../hooks/toast";
import { useSFX } from "../../hooks/useSFX";
import { IBaseModalProps } from "../BaseModal";

type TranslateFn = (...args: any[]) => any;
type AccountData = ReturnType<typeof useGetAccount>["data"];
type MutateAccount = ReturnType<typeof useGetAccount>["mutate"];
type AddToast = ReturnType<typeof useToast>["addToast"];
type PlaySound = ReturnType<typeof useSFX>["playSound"];
type ClosePosition = ReturnType<typeof useOrder>["closePosition"];
type ClosePerpPosition = ReturnType<typeof usePerpOrder>["closePerpPosition"];

interface IRunClosePositionParams {
  position: GetAccount200ResponsePositionsInner | undefined;
  closePerpPosition: ClosePerpPosition;
  closePosition: ClosePosition;
  accountData: AccountData;
  mutateAccount: MutateAccount;
  revalidateAfterPositionClose: () => void;
  onHide: IBaseModalProps["onHide"];
  playSound: PlaySound;
  addToast: AddToast;
  t: TranslateFn;
  apiError: TranslateFn;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
}

export const runClosePosition = async ({
  position,
  closePerpPosition,
  closePosition,
  accountData,
  mutateAccount,
  revalidateAfterPositionClose,
  onHide,
  playSound,
  addToast,
  t,
  apiError,
  setError,
  setLoading,
}: IRunClosePositionParams) => {
  if (!position) {
    return;
  }

  try {
    setError(undefined);
    setLoading(true);

    // Use direct contract call for perp positions
    let txHash: string | undefined;
    if (position.instrument_type === InstrumentTypeResponse.Perpetual ||
        position.instrument_name?.endsWith('-PERP')) {
      const result = await closePerpPosition(position);
      txHash = result?.tx_hash;
    } else {
      await closePosition(position);
    }

    if (accountData) {
      mutateAccount(
        {
          ...accountData,
          positions: (accountData.positions || []).filter(
            (p) =>
              p.instrument_id !== position.instrument_id ||
              p.side !== position.side
          ),
        },
        { revalidate: false }
      );
    }
    mutateAccount();
    setTimeout(() => mutateAccount(), 2000);
    revalidateAfterPositionClose();

    onHide?.();
    playSound("settle", true);
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: t("position_closed_header"),
        subheader: (
          <span>
            {t("position_closed_subheader")}
            {txHash && (
              <>
                {" "}
                <a href={getTxExplorerUrl(txHash)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.link.one, textDecoration: "underline" }}>
                  View transaction
                </a>
              </>
            )}
          </span>
        ),
        status: ToastStatusEnum.SUCCESS,
      },
      4000
    );
  } catch (createError: any) {
    const friendly = formatContractError(createError);
    setError(apiError(friendly) || friendly || t("close_position_error"));
  } finally {
    setLoading(false);
  }
};
